import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
  schema?: Record<string, unknown>;
}

export default function SEO({ title, description, keywords, canonicalUrl, ogType = 'website', ogImage, article, schema }: SEOProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }
    setMeta('og:type', ogType, true);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    if (canonicalUrl) setMeta('og:url', canonicalUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    if (article) {
      if (article.publishedTime) setMeta('article:published_time', article.publishedTime, true);
      if (article.author) setMeta('article:author', article.author);
      if (article.tags) {
        article.tags.forEach(tag => {
          setMeta('article:tag', tag, true);
        });
      }
    }

    if (schema) {
      let scriptEl = document.querySelector('#schema-jsonld');
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = 'schema-jsonld';
        scriptEl.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(schema, null, 2);
    }

    return () => {
      const schemaScript = document.querySelector('#schema-jsonld');
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, article, schema]);

  return null;
}